import {App as CapacitorApp} from "@capacitor/app";
import {Capacitor} from "@capacitor/core";
import * as Sentry from "@sentry/vue";
import {productVersion} from "@finance/shared";
import {diagnosticAttributes, diagnosticHeaders, diagnosticIdentifier, recordDiagnostic, reportWarning, setDiagnosticUser, type DiagnosticAttributes} from "@finance/shared/observability";
import {nativeSession as native} from "./native_session_plugin";

const sessionKey = "sprout.diagnostics.session";
let sessionId = readSession();
let userId: string | null = null;
let screen = "startup";
let nativeUpdates = Promise.resolve();
let nativeDiagnosticsUnavailable = false;

function readSession(): string {
  try {
    const saved = diagnosticIdentifier(window.sessionStorage.getItem(sessionKey));
    if (saved) return saved;
  } catch {
    /* Storage is optional. */
  }
  return newSession();
}

function newSession(): string {
  const id = crypto.randomUUID();
  try {
    window.sessionStorage.setItem(sessionKey, id);
  } catch {
    /* Storage is optional. */
  }
  return id;
}

export function clientDiagnostics(): DiagnosticAttributes {
  return {
    sessionId,
    screen,
    platform: Capacitor.isNativePlatform() ? `${Capacitor.getPlatform()}_app` : "web",
    release: import.meta.env.VITE_APP_RELEASE ?? "development",
    version: productVersion,
    online: typeof navigator === "undefined" || navigator.onLine,
    // An explicit empty ID keeps anonymous work from inheriting a later login.
    userId: userId ?? ""
  };
}

function syncNative(operation?: string): void {
  if (nativeDiagnosticsUnavailable || !Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable("SproutSession")) return;
  const snapshot = {userId, sessionId, screen, release: diagnosticIdentifier(import.meta.env.VITE_APP_RELEASE) ?? "development", ...(operation ? {operation} : {})};
  // Account switches must reach the native scope in order.
  nativeUpdates = nativeUpdates
    .then(() => (nativeDiagnosticsUnavailable ? undefined : native.diagnostics(snapshot)))
    .catch((error: unknown) => {
      // The hosted web bundle can reach an older installed Apple shell before its update.
      if (error && typeof error === "object" && "code" in error && error.code === "UNIMPLEMENTED") {
        nativeDiagnosticsUnavailable = true;
        recordDiagnostic("native.diagnostics.unavailable", clientDiagnostics());
      } else reportWarning(error, "native.diagnostics");
    });
}

export function identifyDiagnostics(id: string | null): void {
  if (userId === id) return;
  if (userId) {
    sessionId = newSession();
    Sentry.getCurrentScope().clearBreadcrumbs();
    Sentry.getIsolationScope().clearBreadcrumbs();
  }
  userId = diagnosticIdentifier(id) ?? null;
  setDiagnosticUser(userId);
  Sentry.setTag("sessionId", sessionId);
  syncNative();
}

export function observeScreen(next: string, previous: string, durationMs: number): void {
  screen = diagnosticIdentifier(next) ?? "unknown";
  Sentry.setTag("screen", screen);
  recordDiagnostic("navigation.completed", {...clientDiagnostics(), previousScreen: diagnosticIdentifier(previous) ?? "unknown", durationMs});
  syncNative();
}

export function diagnosticRequestHeaders(): Record<string, string> {
  return {
    [diagnosticHeaders.requestId]: crypto.randomUUID(),
    [diagnosticHeaders.sessionId]: sessionId,
    [diagnosticHeaders.screen]: screen,
    [diagnosticHeaders.release]: diagnosticIdentifier(import.meta.env.VITE_APP_RELEASE) ?? "development"
  };
}

export function recordUsage(event: string, properties: object = {}): void {
  recordDiagnostic(`usage.${event}`, {...clientDiagnostics(), ...diagnosticAttributes(properties)});
  syncNative(`usage.${event}`);
}

export function initializeClientDiagnostics(): void {
  Sentry.setTags(clientDiagnostics());
  recordDiagnostic("app.started", clientDiagnostics());
  syncNative();
  if (Capacitor.isNativePlatform()) {
    void CapacitorApp.getInfo()
      .then((info) => {
        Sentry.setTags({appVersion: info.version, appBuild: info.build});
        recordDiagnostic("app.native_ready", {...clientDiagnostics(), appVersion: info.version, appBuild: info.build});
      })
      .catch((error: unknown) => reportWarning(error, "native.app_info"));
  }
}
